import {
  Chart,
  type ChartDataset,
  Filler,
  Legend,
  LineController,
  LineElement,
  LinearScale,
  PointElement,
  Tooltip,
} from 'chart.js';
import { type JSX, createEffect, onCleanup, onMount } from 'solid-js';

Chart.register(LineController, LineElement, PointElement, LinearScale, Tooltip, Legend, Filler);

export interface LineSeries {
  label: string;
  /** Hex colour, e.g. '#2563eb'. */
  color: string;
  points: { x: number; y: number | null }[];
  fill?: boolean;
}

export interface LineChartProps {
  series: LineSeries[];
  /** Formats y values on the axis and in the tooltip. */
  formatY: (value: number) => string;
  /** Formats x (epoch ms) on the axis and in the tooltip. */
  formatX: (ms: number) => string;
  yMax?: number;
  height?: number;
  /** Accessible summary, e.g. "CPU % over the last hour". */
  label: string;
}

const datasets = (
  series: LineSeries[],
): ChartDataset<'line', { x: number; y: number | null }[]>[] =>
  series.map((s) => ({
    label: s.label,
    data: s.points,
    borderColor: s.color,
    backgroundColor: `${s.color}22`,
    fill: s.fill ?? false,
    borderWidth: 1.5,
    pointRadius: 0,
    pointHitRadius: 6,
    tension: 0.25,
    // null points (agent offline) stay gaps instead of a straight line across them.
    spanGaps: false,
  }));

/**
 * Time-series line chart (Chart.js, linear x of epoch ms — no date adapter;
 * labels come from the caller's formatter so the user's timezone applies).
 */
export const LineChart = (props: LineChartProps): JSX.Element => {
  let canvas: HTMLCanvasElement | undefined;
  let chart: Chart<'line', { x: number; y: number | null }[]> | undefined;

  onMount(() => {
    const ctx = canvas?.getContext('2d');
    if (!ctx) return; // jsdom / no canvas support
    const dark = document.documentElement.classList.contains('dark');
    const grid = dark ? '#334155' : '#e5e7eb';
    const text = dark ? '#94a3b8' : '#6b7280';
    chart = new Chart<'line', { x: number; y: number | null }[]>(ctx, {
      type: 'line',
      data: { datasets: datasets(props.series) },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        animation: window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
          ? false
          : { duration: 200 },
        interaction: { mode: 'index', intersect: false },
        scales: {
          x: {
            type: 'linear',
            grid: { color: grid },
            ticks: { color: text, maxTicksLimit: 6, callback: (v) => props.formatX(Number(v)) },
          },
          y: {
            min: 0,
            max: props.yMax,
            grid: { color: grid },
            ticks: { color: text, maxTicksLimit: 5, callback: (v) => props.formatY(Number(v)) },
          },
        },
        plugins: {
          legend: {
            display: props.series.length > 1,
            labels: { color: text, boxWidth: 10, boxHeight: 10 },
          },
          tooltip: {
            callbacks: {
              title: (items) => props.formatX(Number(items[0]?.parsed.x)),
              label: (item) => `${item.dataset.label}: ${props.formatY(Number(item.parsed.y))}`,
            },
          },
        },
      },
    });
  });

  createEffect(() => {
    const next = datasets(props.series);
    if (!chart) return;
    chart.data.datasets = next;
    chart.options.scales = {
      ...chart.options.scales,
      y: { ...chart.options.scales?.y, max: props.yMax },
    };
    chart.update();
  });

  onCleanup(() => chart?.destroy());

  return (
    <div style={`height:${props.height ?? 180}px`} class="relative">
      <canvas ref={canvas} role="img" aria-label={props.label} />
    </div>
  );
};
